import {
  MAX_FINAL_PRICE_MINOR,
  type DiscountReason,
  type PaymentDiscountInput,
  type PaymentMethod,
  type PaymentTip,
  type PaymentUpgradeInput,
  type TipMethod,
} from '@360face/domain';
import { parseEurosToMinor } from './gift';

/** Tip and discount fields on the payment screen (owner 15.10.2026). */
export interface ExtrasDraft {
  /** "Change amount" menu: nothing, a discount with a reason, or a typed final amount ("upgrade"). */
  adjust: 'none' | 'discount' | 'upgrade';
  /** Upgrade: the final amount typed by the therapist. */
  finalText: string;
  tipText: string;
  tipMethod: TipMethod;
  /** Quick amount chosen (cents); `undefined` = use `discountText`. */
  discountPreset?: number;
  discountText: string;
  reason: DiscountReason | '';
  note: string;
}

export const emptyExtras = (method: PaymentMethod): ExtrasDraft => ({
  adjust: 'none',
  finalText: '',
  tipText: '',
  tipMethod: method === 'mbway' || method === 'cash' ? method : 'card_terminal',
  discountText: '',
  reason: '',
  note: '',
});

export type ExtrasProblem = 'tip_amount' | 'discount_amount' | 'discount_too_high' | 'reason' | 'note' | 'final_amount';

export interface ExtrasResult {
  tipMinor: number;
  discountMinor: number;
  /** Bill after the discount (what the certificate and the money cover). */
  dueMinor: number;
  /** Money to receive: due + tip (before a certificate). */
  chargeMinor: number;
  problem?: ExtrasProblem;
  /** Upgrade: the typed final amount (when valid). */
  finalMinor?: number;
  extras: { discount?: PaymentDiscountInput; upgrade?: PaymentUpgradeInput; tip?: PaymentTip };
}

export function evalExtras(d: ExtrasDraft, billMinor: number): ExtrasResult {
  const tipMinor = d.tipText.trim() ? (parseEurosToMinor(d.tipText) ?? NaN) : 0;
  if (d.adjust === 'upgrade') return evalUpgrade(d, billMinor, tipMinor);
  if (d.adjust === 'none') d = { ...d, discountPreset: undefined, discountText: '' };
  const discountMinor = d.discountPreset ?? (d.discountText.trim() ? (parseEurosToMinor(d.discountText) ?? NaN) : 0);
  const dueMinor = Math.max(0, billMinor - (Number.isFinite(discountMinor) ? discountMinor : 0));
  const base = {
    tipMinor: Number.isFinite(tipMinor) ? tipMinor : 0,
    discountMinor: Number.isFinite(discountMinor) ? discountMinor : 0,
    dueMinor,
    chargeMinor: dueMinor + (Number.isFinite(tipMinor) ? tipMinor : 0),
    extras: {},
  };
  let problem: ExtrasProblem | undefined;
  if (!Number.isFinite(tipMinor) || (tipMinor > 0 && tipMinor < 100)) problem = 'tip_amount';
  else if (!Number.isFinite(discountMinor) || (discountMinor > 0 && discountMinor < 100)) problem = 'discount_amount';
  else if (discountMinor > billMinor) problem = 'discount_too_high';
  else if (discountMinor > 0 && !d.reason) problem = 'reason';
  else if (discountMinor > 0 && d.reason === 'other' && d.note.trim().length < 3) problem = 'note';
  if (problem) return { ...base, problem };
  return {
    ...base,
    extras: {
      ...(discountMinor > 0
        ? {
            discount: {
              amountMinor: discountMinor,
              reason: d.reason as DiscountReason,
              ...(d.note.trim() ? { note: d.note.trim() } : {}),
            },
          }
        : {}),
      ...(tipMinor > 0 ? { tip: { amountMinor: tipMinor, method: d.tipMethod } } : {}),
    },
  };
}

function evalUpgrade(d: ExtrasDraft, billMinor: number, tipMinor: number): ExtrasResult {
  const typed = parseEurosToMinor(d.finalText);
  const finalMinor = typed !== undefined && typed <= MAX_FINAL_PRICE_MINOR ? typed : undefined;
  const tip = Number.isFinite(tipMinor) ? tipMinor : 0;
  const dueMinor = finalMinor ?? billMinor;
  const base = { tipMinor: tip, discountMinor: 0, dueMinor, chargeMinor: dueMinor + tip, extras: {} };
  if (!Number.isFinite(tipMinor) || (tipMinor > 0 && tipMinor < 100)) return { ...base, problem: 'tip_amount' };
  if (finalMinor === undefined) return { ...base, problem: 'final_amount' };
  return {
    ...base,
    finalMinor,
    extras: {
      ...(finalMinor !== billMinor ? { upgrade: { finalMinor } } : {}),
      ...(tipMinor > 0 ? { tip: { amountMinor: tipMinor, method: d.tipMethod } } : {}),
    },
  };
}
